import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Category } from "../../types";
import { useCategoriesStore } from "../../store/categoriesStore";
import { useProductStore } from "../../store/productStore";

interface HamburgerMenuProps {
  onClose: () => void;
  isMobile: boolean;
}

const HamburgerMenuMobile: React.FC<HamburgerMenuProps> = ({ onClose, isMobile }) => {
  const { categories, fetchCategories } = useCategoriesStore();
  const { setFilter, resetFilters } = useProductStore();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * selectedPath хранит выбранную иерархию.
   * Например, если выбраны "Аксессуары" и затем "Для волос", 
   * то selectedPath = [Аксессуары, Для волос].
   */
  const [selectedPath, setSelectedPath] = useState<Category[]>([]);
  /**
   * expandedLevels – для каждого уровня хранится режим отображения:
   * true — список полностью раскрыт (все варианты видны),
   * false — уровень свернут и показан только выбранный элемент.
   * Изначально уровень 0 раскрыт.
   */
  const [expandedLevels, setExpandedLevels] = useState<boolean[]>([true]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Закрытие меню при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  /**
   * Возвращает список категорий для заданного уровня.
   * Уровень 0 — все родительские категории,
   * для уровня > 0 — дочерние категории выбранной на предыдущем уровне.
   */
  const getCategoriesAtLevel = (level: number): Category[] => {
    if (level === 0) {
      return categories;
    } else {
      const parent = selectedPath[level - 1];
      return parent && parent.children ? parent.children : [];
    }
  };

  /**
   * Обработка клика по категории на уровне level.
   *
   * Если кликнули по НЕ выбранной категории, то:
   *  - Обновляем selectedPath до уровня level, записываем выбранную категорию,
   *  - Помечаем уровень как свернутый (т.е. показываем только выбранный элемент),
   *  - Если у выбранной категории есть дети — для следующего уровня выставляем режим "раскрыт".
   *
   * Если кликнули по уже выбранной категории:
   *  - Если существуют более глубокие уровни, то удаляем их и возвращаем следующий уровень в режим раскрытия.
   *  - Если это самый глубокий выбранный уровень, то снимаем выбор (возвращаем список на этом уровне полностью).
   */
  const handleClick = (category: Category, level: number) => {
    let newPath = [...selectedPath];
    let newExpanded = [...expandedLevels];

    if (newPath[level] && newPath[level].id === category.id) {
      // Категория уже выбрана на этом уровне
      if (newPath.length > level + 1) {
        // Есть выбранные категории глубже – убираем их и делаем следующий уровень раскрытым
        newPath = newPath.slice(0, level + 1);
        newExpanded[level + 1] = true;
      } else {
        // Это самый глубокий уровень – снимаем выбор, чтобы показать полный список
        newPath = newPath.slice(0, level);
        newExpanded[level] = true;
      }
    } else {
      // Новая выбор на этом уровне – обновляем путь
      newPath = newPath.slice(0, level);
      newPath[level] = category;
      // Помечаем уровень как свернутый (т.е. показываем только выбранный элемент)
      newExpanded[level] = false;
      // Если у выбранной категории есть дочерние, для следующего уровня выставляем режим "раскрыт"
      if (category.children && category.children.length > 0) {
        newExpanded[level + 1] = true;
      } else {
        newExpanded = newExpanded.slice(0, level + 1);
      }
    }
    setSelectedPath(newPath);
    setExpandedLevels(newExpanded);
  };

  /**
   * При клике по кнопке "Готово" переходим в каталог,
   * устанавливая фильтр по самой глубокой выбранной категории.
   */
  const handleDone = () => {
    if (selectedPath.length === 0) return;
    const chosenCategory = selectedPath[selectedPath.length - 1];
    resetFilters();
    setFilter("category_id", chosenCategory.id);
    navigate(`/catalog?category_id=${chosenCategory.id}`);
    onClose();
  };

  /**
   * Рендер списка для уровня level.
   * Если для данного уровня выбрана категория и уровень свернут (expandedLevels[level] === false),
   * то показываем только выбранную категорию, иначе — полный список.
   * Добавляем отступ для уровней > 0.
   */
  const renderList = (level: number, cats: Category[]) => {
    let listToRender = cats;
    if (selectedPath[level] && expandedLevels[level] === false) {
      listToRender = [selectedPath[level]!];
    }
    return (
      <ul
        key={level}
        style={{
          listStyle: "none",
          margin: 0,
          padding: "8px 0",
          marginLeft: level * 20, // отступ для визуальной иерархии
          fontSize: "14px",
        }}
      >
        {listToRender.map((cat) => {
          const isSelected = selectedPath[level]?.id === cat.id;
          return (
            <li
              key={cat.id}
              onClick={() => handleClick(cat, level)}
              style={{
                padding: "8px 12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e0e0e0",
                cursor: "pointer",
              }}
            >
              <span>{cat.name}</span>
              <input type="checkbox" readOnly checked={isSelected} />
            </li>
          );
        })}
      </ul>
    );
  };

  /**
   * Определяем, какие уровни для отображения.
   * Всегда рендерим уровень 0.
   * Если на уровне N выбрана категория и у неё есть дочерние, то рендерим уровень N+1.
   * Если на каком‑либо уровне выбор снят (т.е. пользователь открыл список для выбора),
   * то дальнейшие уровни не отображаем.
   */
  const levelsToRender: React.ReactNode[] = [];
  let level = 0;
  while (true) {
    const cats = getCategoriesAtLevel(level);
    if (!cats || cats.length === 0) break;
    levelsToRender.push(renderList(level, cats));

    // Если на данном уровне пользователь ещё не сделал выбор, то дальше рендерить не нужно
    if (!selectedPath[level]) break;

    // Вынесем выбранную категорию для уровня, чтобы избежать ошибки TS
    const currentSelected = selectedPath[level]!;
    // Если выбранная категория не имеет детей, то дальше рендерить не нужно
    if (!currentSelected.children || currentSelected.children.length === 0) break;
    level++;
  }

  return (
    <div className="fixed top-0 left-0 right-0 bottom-[56px] bg-white z-40 flex flex-col">
      {/* Область списка с прокруткой и нижним отступом */}
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="py-4">
          {levelsToRender}
        </div>
      </div>
      {/* Футер с кнопкой "Готово" */}
      <div className="bg-white p-4 border-t">
        <button
          onClick={handleDone}
          className="w-full p-2 bg-[#2C2C2C] text-white rounded"
        >
          Перейти в каталог
        </button>
      </div>
    </div>
  );
};

export default HamburgerMenuMobile;
